// Public component that reads home.upcoming_festivals and renders a
// row of festival cards, each with optional countdown timer to start_date.
// If the slot is empty, renders nothing (page-neutral).

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, ArrowRight } from "lucide-react";
import { API } from "../App";
import { pagePath } from "../lib/pagePath";

const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const target = new Date(isoDate);
  if (isNaN(target)) return null;
  const now = new Date();
  const ms = target.getTime() - now.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

const FestivalCard = ({ item }) => {
  const f = item.record;
  const showCountdown = item.meta?.show_countdown ?? true;
  const d = daysUntil(f.start_date);
  const badgeText = d == null ? null : d > 0 ? `${d} day${d === 1 ? "" : "s"} to go` : d === 0 ? "Today!" : `${-d} day${d === -1 ? "" : "s"} ago`;
  const href = pagePath(f);
  return (
    <Link to={href}
      className="group block rounded-2xl overflow-hidden bg-white border border-[#E2E0DC] shadow-sm hover:shadow-md transition-shadow"
      data-testid={`upcoming-fest-${f.slug}`}
    >
      <div className="relative h-40 md:h-48 bg-[#10233A]"
        style={f.hero_image ? { backgroundImage: `linear-gradient(rgba(10,22,38,0.35), rgba(27,58,92,0.7)), url(${f.hero_image})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
        {showCountdown && badgeText && d != null && d >= 0 && (
          <span className="absolute top-3 right-3 bg-[#F2CC8F] text-[#1A1A1A] text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow">
            {badgeText}
          </span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-['Playfair_Display'] text-xl md:text-2xl font-semibold text-[#1A1A1A] group-hover:text-[#E76F51] transition-colors">
          {f.title}
        </h3>
        {f.tagline && <p className="text-sm italic text-[#5C5C5C] mt-1">{f.tagline}</p>}
        <div className="flex items-center gap-4 mt-3 text-xs text-[#5C5C5C]">
          {f.start_date && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {f.start_date}{f.end_date && f.end_date !== f.start_date ? ` – ${f.end_date}` : ""}
            </span>
          )}
          {f.home_city && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {f.home_city}
            </span>
          )}
        </div>
        <span className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-[#E76F51] group-hover:gap-2 transition-all">
          Open festival <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
};

const UpcomingFestivalsRow = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/feature-slots/home.upcoming_festivals`)
      .then((r) => setItems((r.data?.items || []).filter((it) => it.record)))
      .catch(() => setItems([]));
  }, []);
  if (items.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-10" data-testid="home-upcoming-festivals">
      <div className="flex items-end justify-between mb-5">
        <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-semibold text-[#1A1A1A]">
          Upcoming Festivals
        </h2>
        <Link to="/community-hub" className="text-sm text-[#2E5C55] underline">
          See all events →
        </Link>
      </div>
      <div className={`grid gap-4 ${items.length === 1 ? "" : items.length === 2 ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"}`}>
        {items.map((it) => (
          <FestivalCard key={`${it.type}:${it.ref_id}`} item={it} />
        ))}
      </div>
    </section>
  );
};

export default UpcomingFestivalsRow;
